import React, {useContext, useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {selectProfile} from "../../../ClientManagement/ClientProfile/activeProfileSlice";
import {assetsApiClient} from "../../AssetsApiClient";
import {
    EMPTY_INVESTMENT_PROGRAM_RESPONSE,
    InvestmentProgram,
    InvestmentProgramsResponse
} from "../../models/InvestmentProgram";
import LoadingIndicator from "../../../pages/LoadingIndicator";
import {useAppInsights} from "../../../AppInsights";
import AddInvestmentProgram from "./AddInvestmentProgram";
import NoInvestmentProgram from "./NoInvestmentProgram";
import AssetsViewContext from "../../common/AssetsViewContext";
import useAnalytics from "../../../utils/useAnalytics";
import {setHasInvalidIp, setIpStatusInfo} from "../../common/ipStatusSlice";
import {selectReleaseToggles} from "../../../ReleaseToggles/releaseTogglesSlice";

export type SearchParams = {
    team: boolean,
    page: number,
    pageSize: number,
    groupName?: string,
    portfolioManager?: string
};

export default function AddInvestmentProgramContainer() {
    const history = useHistory();
    const profile = useAppSelector(selectProfile);
    const viewType = useContext(AssetsViewContext);
    const appInsights = useAppInsights();
    const [myInvestmentPrograms, setMyInvestmentPrograms] = useState<InvestmentProgramsResponse>(EMPTY_INVESTMENT_PROGRAM_RESPONSE);
    const [teamInvestmentPrograms, setTeamInvestmentPrograms] = useState<InvestmentProgramsResponse>(EMPTY_INVESTMENT_PROGRAM_RESPONSE);
    const [receivedInvestmentProgramResponses, setReceivedInvestmentProgramResponses] = useState<boolean>(false);
    const [isValidatingBeforeSaving, setIsValidatingBeforeSaving] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [detailErrorMessage, setDetailErrorMessage] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasAccessToInvestmentProgram, setHasAccessToInvestmentProgram] = useState(true);
    const [displayNoMatchFound, setDisplayNoMatchFound] = useState(false);
    const releaseToggles = useAppSelector(selectReleaseToggles);
    const dispatch = useAppDispatch();

    useAnalytics(
        "Navigate_AddInvestmentProgram",
        "Navigate_AddInvestmentProgram_TimeSpent",
        "enableAssetsGoogleAnalytics"
    );

    useEffect(() => {
        appInsights.startTrackPage("ViewInvestmentProgram");
        setIsLoading(true);
        Promise.all([
            assetsApiClient.getInvestmentPrograms(profile.id, {team: false, page: 1, pageSize: 25}),
            assetsApiClient.getInvestmentPrograms(profile.id, {team: true, page: 1, pageSize: 25})
        ])
            .then(([myInvestmentProgramResponse, teamInvestmentProgramResponse]) => {
                setMyInvestmentPrograms(myInvestmentProgramResponse);
                setTeamInvestmentPrograms(teamInvestmentProgramResponse);
                setReceivedInvestmentProgramResponses(true);
                setHasAccessToInvestmentProgram(myInvestmentProgramResponse.investmentPrograms.length > 0 || teamInvestmentProgramResponse.investmentPrograms.length > 0)
            })
            .catch(reason => console.log(reason))
            .finally(() => {
                stopTrackingViewInvestmentProgram();
                setIsLoading(false);
            });
    }, []);

    const stopTrackingViewInvestmentProgram = () => {
        return () => {
            appInsights.stopTrackPage("ViewInvestmentProgram", window.location.href, {
                profileId: profile.id,
            });
        };
    }

    const onSave = (investmentProgram: InvestmentProgram, isTeam: boolean) => {
        if (investmentProgram) {
            appInsights.startTrackPage("AddInvestmentProgram")
            setIsValidatingBeforeSaving(true);

            assetsApiClient.postInvestmentProgram({
                investmentProgramId: investmentProgram?.id,
                profileId: profile.id,
                isTeam: isTeam,
            }).then(response => {
                setIsValidatingBeforeSaving(false);
                if (response.status === 201) {
                        assetsApiClient.getInvestmentProgramStatus(profile.id)
                            .then(response => {
                                const invalidStatuses = [
                                    "Invalid",
                                    "Invalid-Pen.Approval",
                                    "InvalidPenApproval"
                                ];
                                dispatch(setHasInvalidIp(invalidStatuses.includes(response.ipStatus)));
                                dispatch(setIpStatusInfo(response));
                            })
                    history.push(`/Profile/${profile.id}/ClientProfile/${viewType}`);
                }
            }).catch(reason => {
                setIsValidatingBeforeSaving(false);

                switch (reason.status) {
                    case 409:
                        setErrorMessage('Unable to add the Investment Program.')
                        setDetailErrorMessage('It is already linked to another profile.')
                        break;
                    case 404:
                        setErrorMessage('Unable to add the Investment Program.')
                        setDetailErrorMessage('')
                        break;
                    case 400:
                        setErrorMessage('Unable to add the Investment Program.')
                        setDetailErrorMessage('One or more accounts are already linked to this profile or proposal.')
                        break;
                }
            }).finally(() =>
                appInsights.stopTrackPage("AddInvestmentProgram", window.location.href, {
                    investmentProgramId: investmentProgram?.id
                })
            );
        }
    }

    const onSearch = (searchParams: SearchParams) => {
        setIsLoading(true);
        setDisplayNoMatchFound(false);
        assetsApiClient.getInvestmentPrograms(profile.id, searchParams)
            .then(investmentProgramsResponse => {
                setDisplayNoMatchFound(investmentProgramsResponse.investmentPrograms?.length === 0);
                if (searchParams.team) {
                    setTeamInvestmentPrograms(investmentProgramsResponse);
                } else {
                    setMyInvestmentPrograms(investmentProgramsResponse);
                }
            })
            .catch(reason => console.log(reason))
            .finally(() => {
                setIsLoading(false);
            })
    }

    const onCancel = () => {
        history.push(`/Profile/${profile.id}/ClientProfile/${viewType}`);
    };

    if (!receivedInvestmentProgramResponses) {
        return <LoadingIndicator className="investment-programs-loading-indicator" />;
    }

    if (isValidatingBeforeSaving) {
        return <LoadingIndicator
            className="loading-container"
            displayMessage={"Validating Investment Program, loading Legal Agreements and Held Away Accounts..."}/>;
    }

    if (!hasAccessToInvestmentProgram) {
        return (
            <NoInvestmentProgram onCancel={onCancel} />
        )
    }

    return (
        <div className="investment-programs">
            <AddInvestmentProgram
                onSearch={onSearch}
                onCancel={onCancel}
                onSave={onSave}
                errorMessage={errorMessage}
                detailErrorMessage={detailErrorMessage}
                myInvestmentPrograms={myInvestmentPrograms}
                teamInvestmentPrograms={teamInvestmentPrograms}
                isLoading={isLoading}
                displayNoMatchFound={displayNoMatchFound}
            />
        </div>
    )
}
